import React, { useMemo, useState } from "react";
import { GitCompare, ChevronRight, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  TLP_COMPARE_CATEGORIES,
  TLP_LEADER_CHART_COLORS,
  TLP_DETAIL_TITLES,
  tlpExtractLeaderSummary,
  tlpFormatCompareValue,
  tlpGetLeaderDetailRows,
} from "@/utils/tlpLeaderComparison";

const formatDurationCell = (sec) => {
  const s = Math.max(0, Number(sec) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}s ${m}dk`;
  return `${m} dk`;
};

const renderCellValue = (label, val) => {
  if (label.includes("gün") || label === "Görüşme/gün" || label === "Mola/gün") {
    return Number.isFinite(Number(val)) ? formatDurationCell(val) : String(val ?? "—");
  }
  if (label === "Ciro") return tlpFormatCompareValue("money", val);
  return String(val ?? "—");
};

const AgentDetailTable = ({ rows, isDark, textClass, labelClass }) => (
  <div className="overflow-auto rounded-lg border text-xs">
    <table className="w-full min-w-[420px]">
      <thead className={isDark ? "bg-slate-800 text-slate-300 sticky top-0" : "bg-gray-100 sticky top-0"}>
        <tr>
          <th className="text-left px-3 py-2 font-semibold">Asistan</th>
          {(rows[0]?.cols || []).map(([label]) => (
            <th key={label} className="text-right px-3 py-2 font-semibold">{label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={10} className={`px-3 py-6 text-center ${labelClass}`}>Asistan verisi yok.</td>
          </tr>
        ) : rows.map((row, i) => (
          <tr key={`${row.name}-${i}`} className={isDark ? "border-t border-slate-800" : "border-t border-gray-100"}>
            <td className={`px-3 py-1.5 font-medium ${textClass}`}>
              {row.name}
              {row.meta ? (
                <span className={`ml-1 text-[10px] font-normal ${labelClass}`}>({row.meta})</span>
              ) : null}
            </td>
            {row.cols.map(([label, val]) => (
              <td key={label} className="px-3 py-1.5 text-right tabular-nums">
                {renderCellValue(label, val)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TlpCompareBarRow = ({ metric, entries, isDark, textClass, labelClass, onLeaderClick }) => {
  const numericVals = entries.map((e) => {
    const v = e.summary[metric.key];
    return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
  });
  const max = Math.max(...numericVals.filter((v) => v != null), 1);

  return (
    <div
      className={`rounded-xl border p-3 md:p-4 ${
        isDark ? "border-slate-800 bg-slate-900/50" : "border-gray-200 bg-white shadow-sm"
      }`}
    >
      <div className="mb-3">
        <h4 className={`text-sm font-semibold ${textClass}`}>{metric.label}</h4>
        {metric.hint ? <p className={`text-[10px] mt-0.5 ${labelClass}`}>{metric.hint}</p> : null}
        <p className={`text-[10px] mt-1 ${labelClass}`}>Lider adına tıklayın → o liderin asistan detayı</p>
      </div>
      <div className="space-y-2.5">
        {entries.map((entry, idx) => {
          const raw = entry.summary[metric.key];
          const num = raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;
          const pct = num != null ? Math.min(100, (num / max) * 100) : 0;
          const color = TLP_LEADER_CHART_COLORS[idx % TLP_LEADER_CHART_COLORS.length];
          let display = metric.format === "duration" && num != null
            ? formatDurationCell(num)
            : tlpFormatCompareValue(metric.format, raw);
          if (metric.key === "avgExitTenureDays" && entry.summary.avgExitTenureLabel && num != null) {
            display = `${display} (${entry.summary.avgExitTenureLabel})`;
          }
          const isBest = metric.higherBetter != null && num != null
            && num === Math.max(...numericVals.filter((v) => v != null));
          const isWorst = metric.higherBetter === false && num != null
            && num === Math.min(...numericVals.filter((v) => v != null));

          return (
            <div key={entry.leaderId} className="grid grid-cols-[minmax(0,7rem)_1fr_auto] gap-2 items-center text-xs">
              <button
                type="button"
                className={`truncate font-medium text-left hover:underline ${color.text} ${textClass}`}
                title={`${entry.leaderName} — asistan detayı`}
                onClick={() => onLeaderClick(entry, metric)}
              >
                {entry.leaderName}
              </button>
              <div className={`h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                <div
                  className={`h-full rounded-full transition-all duration-700 ${color.bar}`}
                  style={{ width: num != null ? `${pct}%` : "0%" }}
                />
              </div>
              <span
                className={`tabular-nums font-semibold min-w-[4.5rem] text-right ${
                  isBest
                    ? (isDark ? "text-emerald-300" : "text-emerald-700")
                    : isWorst
                      ? (isDark ? "text-amber-300" : "text-amber-700")
                      : textClass
                }`}
              >
                {display}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TlpLeaderComparisonView = ({
  compareEntries,
  startDate,
  endDate,
  isDark,
  textClass,
  labelClass,
  formatDisplayDate,
}) => {
  const [detailModal, setDetailModal] = useState(null);

  const entries = useMemo(
    () => compareEntries.map((e) => ({
      ...e,
      summary: tlpExtractLeaderSummary(e.bundle),
    })),
    [compareEntries],
  );

  const leaderTabs = entries.map((e, i) => ({
    ...e,
    color: TLP_LEADER_CHART_COLORS[i % TLP_LEADER_CHART_COLORS.length],
  }));

  const openLeaderMetricDetail = (leaderEntry, metric) => {
    const category = TLP_COMPARE_CATEGORIES.find((c) => c.metrics.some((m) => m.key === metric.key));
    setDetailModal({
      mode: "leader",
      leaderId: leaderEntry.leaderId,
      leaderName: leaderEntry.leaderName,
      bundle: leaderEntry.bundle,
      detailKey: category?.detailKey || metric.detailKey,
      metricLabel: metric.label,
      categoryTitle: category?.title,
    });
  };

  const openCategoryDetail = (category) => {
    setDetailModal({
      mode: "category",
      category,
      entries: leaderTabs,
    });
  };

  const modalRows = detailModal?.mode === "leader" && detailModal.bundle
    ? tlpGetLeaderDetailRows(detailModal.bundle, detailModal.detailKey)
    : [];

  return (
    <div className="space-y-6">
      <div
        className={`rounded-2xl border overflow-hidden ${
          isDark
            ? "border-emerald-500/25 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/40"
            : "border-emerald-200 bg-gradient-to-br from-white via-emerald-50/50 to-teal-50/40 shadow-sm"
        }`}
      >
        <div className="px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`p-2.5 rounded-xl ${isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>
              <GitCompare className="w-6 h-6" />
            </div>
            <div>
              <h2 className={`text-lg font-bold ${textClass}`}>Takım lideri kıyaslaması</h2>
              <p className={`text-xs mt-0.5 ${labelClass}`}>
                {entries.length} lider · 6 modül · {formatDisplayDate(startDate)} — {formatDisplayDate(endDate)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {leaderTabs.map((e) => (
              <span
                key={e.leaderId}
                className={`text-[11px] font-medium px-2.5 py-1 rounded-full ring-1 ${e.color.ring} ${
                  isDark ? "bg-slate-900/80 text-slate-200" : "bg-white text-gray-800"
                }`}
              >
                {e.leaderName}
                <span className="ml-1.5 tabular-nums opacity-70">{e.summary.agentCount} asistan</span>
              </span>
            ))}
          </div>
        </div>

        <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
          {entries.map((e, idx) => {
            const s = e.summary;
            const color = TLP_LEADER_CHART_COLORS[idx % TLP_LEADER_CHART_COLORS.length];
            return (
              <div
                key={e.leaderId}
                className={`rounded-xl border p-3 ${isDark ? "border-slate-800 bg-slate-950/50" : "border-gray-100 bg-white/90"}`}
              >
                <p className={`text-[10px] font-semibold truncate mb-2 ${color.text}`}>{e.leaderName}</p>
                <p className={`text-lg font-bold tabular-nums ${textClass}`}>
                  {tlpFormatCompareValue("money", s.ciro)}
                </p>
                <p className={`text-[10px] ${labelClass}`}>Ciro · {s.islem} işlem</p>
              </div>
            );
          })}
        </div>
      </div>

      {TLP_COMPARE_CATEGORIES.map((category) => (
        <section
          key={category.id}
          className={`rounded-2xl border p-4 ${isDark ? "border-slate-800 bg-slate-900/40" : "border-gray-200 bg-white shadow-sm"}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className={`text-sm font-bold ${textClass}`}>{category.title}</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={`h-8 text-xs gap-1 ${isDark ? "border-slate-700" : ""}`}
              onClick={() => openCategoryDetail(category)}
            >
              <Users className="w-3.5 h-3.5" />
              Tüm liderler — asistan detayı
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {category.metrics.map((metric) => (
              <TlpCompareBarRow
                key={metric.key}
                metric={metric}
                entries={entries}
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
                onLeaderClick={openLeaderMetricDetail}
              />
            ))}
          </div>
        </section>
      ))}

      <Dialog
        open={Boolean(detailModal)}
        onOpenChange={(open) => { if (!open) setDetailModal(null); }}
      >
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
          {detailModal?.mode === "category" ? (
            <>
              <DialogHeader>
                <DialogTitle className={textClass}>
                  {TLP_DETAIL_TITLES[detailModal.category.detailKey] || detailModal.category.title}
                </DialogTitle>
              </DialogHeader>
              <p className={`text-xs ${labelClass} -mt-2`}>{detailModal.category.title} · tüm seçili liderler</p>
              <div className="overflow-auto flex-1 min-h-0 mt-2 space-y-5 pr-1">
                {detailModal.entries.map((e, idx) => {
                  const rows = tlpGetLeaderDetailRows(e.bundle, detailModal.category.detailKey);
                  const color = TLP_LEADER_CHART_COLORS[idx % TLP_LEADER_CHART_COLORS.length];
                  return (
                    <div key={e.leaderId}>
                      <p className={`text-sm font-semibold mb-2 sticky top-0 py-1 ${color.text} ${isDark ? "bg-slate-900" : "bg-white"}`}>
                        {e.leaderName}
                        <span className={`ml-2 text-xs font-normal ${labelClass}`}>{rows.length} asistan</span>
                      </p>
                      <AgentDetailTable rows={rows} isDark={isDark} textClass={textClass} labelClass={labelClass} />
                    </div>
                  );
                })}
              </div>
            </>
          ) : detailModal?.mode === "leader" ? (
            <>
              <DialogHeader>
                <DialogTitle className={textClass}>
                  {TLP_DETAIL_TITLES[detailModal.detailKey] || "Asistan detayı"}
                </DialogTitle>
              </DialogHeader>
              <p className={`text-xs ${labelClass} -mt-2`}>
                <span className={`font-semibold ${textClass}`}>{detailModal.leaderName}</span>
                {detailModal.categoryTitle ? ` · ${detailModal.categoryTitle}` : ""}
                {detailModal.metricLabel ? ` · ${detailModal.metricLabel}` : ""}
              </p>
              <div className="overflow-auto flex-1 min-h-0 mt-2">
                <AgentDetailTable rows={modalRows} isDark={isDark} textClass={textClass} labelClass={labelClass} />
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TlpLeaderComparisonView;
